import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../../config';


interface GlobalApiKey {
  id: number;
  name: string;
  service: string;
  key_prefix: string;
  is_global: boolean;
  created_at: string;
  created_by?: string;
  last_used?: string;
}

interface SupportedService {
  value: string;
  label: string;
  icon: string;
}

const SUPPORTED_SERVICES: SupportedService[] = [
  { value: 'anthropic', label: 'Anthropic (Claude)', icon: '🧠' },
  { value: 'openai', label: 'OpenAI (GPT)', icon: '🤖' },
  { value: 'azure_speech', label: 'Azure Speech (TTS) ⭐ FREE 0.5M/měsíc', icon: '🎙️' },
  { value: 'google_cloud', label: 'Google Cloud (TTS)', icon: '🎙️' },
  { value: 'nvidia', label: 'NVIDIA AI', icon: '🎮' },
  { value: 'google', label: 'Google (Gemini)', icon: '🔍' },
  { value: 'cohere', label: 'Cohere', icon: '📝' },
  { value: 'cursor', label: 'Cursor AI', icon: '✨' },
  { value: 'custom', label: 'Custom API', icon: '⚙️' },
];

const GlobalApiKeys: React.FC = () => {
  const [apiKeys, setApiKeys] = useState<GlobalApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyService, setNewKeyService] = useState('');
  const [newKeyValue, setNewKeyValue] = useState('');
  const [creating, setCreating] = useState(false);
  const [models, setModels] = useState<{[key: number]: any}>({});
  const [loadingModels, setLoadingModels] = useState<{[key: number]: boolean}>({});

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/api-keys`);
      setApiKeys(response.data);

      // Auto-load models for each key
      response.data.forEach((key: GlobalApiKey) => {
        fetchModels(key.id);
      });
    } catch (err) {
      console.error('Error fetching API keys:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async () => {
    if (!newKeyName.trim() || !newKeyService.trim() || !newKeyValue.trim()) {
      alert('Please fill in all fields');
      return;
    }

    try {
      setCreating(true);
      await axios.post(`${API_URL}/api/admin/api-keys`, {
        name: newKeyName,
        service: newKeyService,
        api_key: newKeyValue,
        is_global: true
      });
      setNewKeyName('');
      setNewKeyService('');
      setNewKeyValue('');
      setShowCreateModal(false);
      fetchApiKeys();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const fetchModels = async (keyId: number) => {
    if (models[keyId]) {
      return; // Already loaded
    }

    setLoadingModels(prev => ({ ...prev, [keyId]: true }));
    try {
      const response = await axios.get(`${API_URL}/api/keys/${keyId}/models`);
      setModels(prev => ({ ...prev, [keyId]: response.data }));
    } catch (err) {
      console.error('Failed to fetch models:', err);
    } finally {
      setLoadingModels(prev => ({ ...prev, [keyId]: false }));
    }
  };

  const handleDeleteKey = async (keyId: number) => {
    if (!window.confirm('Are you sure you want to delete this global API key? This will affect all users.')) return;

    try {
      await axios.delete(`${API_URL}/api/admin/api-keys/${keyId}`);
      fetchApiKeys();
    } catch (err) {
      alert('Failed to delete API key');
    }
  };

  return (
    <div className="bg-white dark:bg-gray-600 rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-sans font-bold text-gray-800 dark:text-white normal-case">Global API Keys</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 font-sans">Manage global API keys for all users</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-green-400 hover:bg-green-500 text-gray-800 px-4 py-2 rounded font-sans font-semibold transition"
        >
          + Add Global Key
        </button>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {apiKeys.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400 font-sans">
              No global API keys yet. Click "Add Global Key" to create one.
            </div>
          ) : (
            apiKeys.map((key) => {
              const serviceInfo = SUPPORTED_SERVICES.find(s => s.value === key.service);
              const keyModels = models[key.id];

              return (
                <div key={key.id} className="bg-green-50 dark:bg-green-900/30 border border-green-300 dark:border-green-400 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-2xl">{serviceInfo?.icon || '🔑'}</span>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-sans font-semibold text-gray-800 dark:text-white">{key.name}</h4>
                            <span className="bg-purple-200 dark:bg-purple-300 text-gray-800 px-2 py-0.5 rounded text-xs font-sans font-semibold">
                              Global
                            </span>
                          </div>
                          <p className="text-xs text-gray-600 dark:text-gray-300 font-sans">{serviceInfo?.label || key.service}</p>
                        </div>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-green-600 dark:text-green-400 font-semibold">✓</span>
                          <span className="text-gray-700 dark:text-gray-200 font-sans">
                            API Key: <code className="bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded text-xs font-mono">{key.key_prefix}...</code>
                          </span>
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-300 font-sans">
                          Created: {new Date(key.created_at).toLocaleDateString()}
                          {key.last_used && ` • Last used: ${new Date(key.last_used).toLocaleDateString()}`}
                        </div>
                        <div className="text-xs text-purple-700 dark:text-purple-300 font-sans">
                          <strong>This key is available to all users in the system</strong>
                        </div>

                        {/* Available Models as Badges */}
                        <div className="mt-3">
                          {loadingModels[key.id] ? (
                            <div className="text-xs text-gray-500 dark:text-gray-400 font-sans">Loading models...</div>
                          ) : keyModels?.models ? (
                            <div>
                              <div className="text-xs text-gray-600 dark:text-gray-400 font-sans font-semibold mb-2">
                                Available Models ({keyModels.models.length}):
                              </div>
                              <div className="flex flex-wrap gap-1.5">
                                {keyModels.models.map((model: any) => (
                                  <span
                                    key={model.id}
                                    className="inline-flex items-center gap-1.5 bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-100 px-2 py-1 rounded text-xs font-sans"
                                    title={model.id}
                                  >
                                    <span className="font-semibold">{model.name}</span>
                                    <span className="text-blue-600 dark:text-blue-300">({model.context})</span>
                                  </span>
                                ))}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteKey(key.id)}
                      className="bg-red-400 hover:bg-red-500 text-gray-800 px-3 py-1 rounded text-xs font-sans font-semibold transition ml-4"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white dark:bg-gray-700 rounded-lg p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-sans font-bold text-gray-800 dark:text-white mb-4">Add Global API Key</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-sans font-semibold text-gray-700 dark:text-gray-300 mb-1">Name</label>
                <input
                  type="text"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="e.g., Company OpenAI Key"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-600 text-gray-800 dark:text-white font-sans"
                />
              </div>
              <div>
                <label className="block text-sm font-sans font-semibold text-gray-700 dark:text-gray-300 mb-1">Service</label>
                <select
                  value={newKeyService}
                  onChange={(e) => setNewKeyService(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-600 text-gray-800 dark:text-white font-sans"
                >
                  <option value="">Select a service...</option>
                  {SUPPORTED_SERVICES.map((service) => (
                    <option key={service.value} value={service.value}>
                      {service.icon} {service.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-sans font-semibold text-gray-700 dark:text-gray-300 mb-1">API Key</label>
                <input
                  type="password"
                  value={newKeyValue}
                  onChange={(e) => setNewKeyValue(e.target.value)}
                  placeholder="sk-..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-600 text-gray-800 dark:text-white font-mono"
                />
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded p-3">
                <p className="text-sm text-purple-800 dark:text-purple-300 font-sans">
                  <strong>Global Key:</strong> This key will be available to all users in the system.
                </p>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button
                onClick={handleCreateKey}
                disabled={creating}
                className="flex-1 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 text-white px-4 py-2 rounded font-sans font-semibold transition"
              >
                {creating ? 'Creating...' : 'Create Global Key'}
              </button>
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-800 dark:text-white px-4 py-2 rounded font-sans font-semibold transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalApiKeys;
